// Client-scope Narrative Standards manager for the Client Billing Review drill-down (ported from
// Botfield's NarrativeStandardsManager). Lists the client's own standards (staged edits overlaid,
// with a "Pending approval" tag) plus the billing parent's group standards (read-only,
// "Inherited"). Add / edit / delete are STAGED for Billing Team approval — see
// NarrativeEditModal and billingChangeApi.js. The edit modal stacks above this one.

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../../components/Modal';
import { useAuth } from '../../../auth/AuthContext';
import PendingTag from '../PendingTag';
import { submitChange, withdrawChange, wasWithdrawn } from '../billingChangeApi';
import useNarrativeView from './useNarrativeView';
import NarrativeEditModal from './NarrativeEditModal';
import NarrativeStandardsHelp from './NarrativeStandardsHelp';
import NarrativeHtml from './NarrativeHtml';
import { narrativeText } from './narrativeValidation';
import { useEscLayer } from './narrativeLayers';
import './narratives.css';

// Open-ended service codes get a stable colour derived from the value (same idea as Botfield).
function serviceStyle(v) {
  let h = 0;
  const s = String(v);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return { background: `hsl(${h} 68% 93%)`, color: `hsl(${h} 50% 30%)`, borderColor: `hsl(${h} 45% 80%)` };
}

const LEVEL_CLS = { JOB: 'job', SERV: 'serv' };
const TYPE_CLS = { TIME: 'time', DISB: 'disb', ALL: 'all' };
const uniqSorted = arr => Array.from(new Set(arr)).sort((a, b) => String(a).localeCompare(String(b)));

export default function NarrativeStandardsModal({ open, onClose, client, period, readOnly, onChanged }) {
  const { email } = useAuth();
  const { view, setView, error, initialLoading, refetch } = useNarrativeView({
    period, contIndex: client?.contIndex, enabled: !!open,
  });

  const [editor, setEditor] = useState(null);          // null | { row: record | null, draft?: the refused submission, to retry }
  const [busy, setBusy] = useState(null);              // uuid with a (non-optimistic) withdraw in flight
  const [levelFilter, setLevelFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [servFilter, setServFilter] = useState('');
  const [text, setText] = useState('');

  useEscLayer(!!open, () => onClose?.());
  useEffect(() => { if (!open) setEditor(null); }, [open]);

  const rows = useMemo(() => view?.rows || [], [view]);
  const jobLabels = useMemo(() => {
    const m = {};
    for (const j of (view?.jobs || [])) m[j.key] = j.label;
    return m;
  }, [view]);
  const servTitles = useMemo(() => {
    const m = {};
    for (const s of (view?.services || [])) m[s.key] = s.meta || '';
    return m;
  }, [view]);

  const jobName = i => jobLabels[i] || `Job #${i} (not active)`;
  const jobsOf = r => (r.Idx || []).map(jobName);

  const filtered = useMemo(() => {
    const needle = text.trim().toLowerCase();
    return rows
      .filter(r => !levelFilter || r.Level === levelFilter)
      .filter(r => !typeFilter || r.Type === typeFilter)
      .filter(r => !servFilter || r.Serv === servFilter)
      .filter(r => {
        if (!needle) return true;
        if (narrativeText(r.Narrative).toLowerCase().includes(needle)) return true;
        if (String(r.Serv).toLowerCase().includes(needle)) return true;
        return r.Level === 'JOB' && (r.Idx || []).map(i => jobLabels[i] || '').join(' ').toLowerCase().includes(needle);
      })
      .sort((a, b) =>
        (a._inherited ? 1 : 0) - (b._inherited ? 1 : 0)
        || String(a.Serv).localeCompare(String(b.Serv))
        || (a.Level === b.Level ? 0 : a.Level === 'SERV' ? -1 : 1)
        || String(a.Type).localeCompare(String(b.Type)));
  }, [rows, levelFilter, typeFilter, servFilter, text, jobLabels]);

  const filtersOn = !!(levelFilter || typeFilter || servFilter || text);
  function clearFilters() { setLevelFilter(''); setTypeFilter(''); setServFilter(''); setText(''); }

  const locked = !!view?.locked;
  const canEdit = !readOnly && !!view && !locked;

  async function refreshAll() {
    await refetch();
    onChanged?.();
  }

  // Row-level optimistic helpers. `setView` is the hook's setter; restoring puts back the ONE row (or
  // removes the one that was added) so a refused save doesn't disturb anything else.
  const patchRows = fn => setView(v => (v ? { ...v, rows: fn(v.rows || []) } : v));
  const restoreRow = (uuid, original) => patchRows(rows => (original
    ? (rows.some(r => !r._inherited && r.uuid === uuid)
        ? rows.map(r => (!r._inherited && r.uuid === uuid ? original : r))
        : [...rows, original])
    : rows.filter(r => r._inherited || r.uuid !== uuid)));
  const optimisticTag = op => ({ changeId: null, op, requestedBy: email || '', requestedAt: new Date().toISOString(), optimistic: true });

  // The edit form's optimistic hook: show the submitted standard (with its "Pending approval" tag) at
  // once. Returns the function that puts the list back if the server refuses.
  function showSubmitted(draft, original) {
    patchRows(rows => {
      const next = {
        ...(original || {}),
        Level: draft.Level, Idx: draft.Idx, Type: draft.Type, Serv: draft.Serv, Narrative: draft.Narrative,
        uuid: draft.uuid, ApplyToGroup: draft.ApplyToGroup, Scope: 'CLIENT',
        _pending: optimisticTag('set'), _pendingDelete: false,
      };
      return original && rows.some(r => !r._inherited && r.uuid === draft.uuid)
        ? rows.map(r => (!r._inherited && r.uuid === draft.uuid ? next : r))
        : [...rows, next];
    });
    return () => restoreRow(draft.uuid, original || null);
  }

  // Optimistic: the row reads "pending deletion" at once; a refusal puts it back and shows the message.
  async function remove(row) {
    if (!canEdit || row._pending?.optimistic) return;
    const preview = narrativeText(row.Narrative).slice(0, 160);
    if (!window.confirm(`Request deletion of this narrative standard?\n\n"${preview}"\n\nThe Billing Team will review the request.`)) return;
    patchRows(rows => rows.map(r => (!r._inherited && r.uuid === row.uuid
      ? { ...r, _pendingDelete: true, _pending: optimisticTag('delete') } : r)));
    try {
      // Deleting a standard that only exists as an open request just withdraws that request.
      const out = await submitChange({
        period, contIndex: client.contIndex, section: 'narrative', op: 'delete',
        payload: { uuid: row.uuid },
      });
      toast.success(wasWithdrawn(out) ? 'Request withdrawn.' : 'Deletion submitted for Billing Team approval.');
      await refreshAll();
    } catch (err) {
      restoreRow(row.uuid, row);
      toast.error(err.message, { autoClose: 10000 });
    }
  }

  // Withdrawing a pending DELETE just brings the live row back, so that is optimistic; withdrawing a
  // pending add/edit would need the previous text (the server doesn't send it), so it waits for the server.
  async function withdraw(row, change) {
    if (busy || change.changeId == null) return;
    const optimistic = change.op === 'delete';
    if (optimistic) {
      patchRows(rows => rows.map(r => (!r._inherited && r.uuid === row.uuid ? { ...r, _pendingDelete: false, _pending: null } : r)));
    } else {
      setBusy(row.uuid);
    }
    try {
      await withdrawChange({ period, changeId: change.changeId });
      toast.success('Request withdrawn.');
      await refreshAll();
    } catch (err) {
      if (optimistic) restoreRow(row.uuid, row);
      toast.error(err.message, { autoClose: 10000 });
    } finally {
      if (!optimistic) setBusy(null);
    }
  }

  function renderPending(row) {
    const p = row._pending;
    if (!p) return null;
    const change = {
      ...p,
      summary: `Pending ${p.op === 'delete' ? 'deletion' : 'edit'} (editing or deleting this row again replaces this request)`,
    };
    return (
      <>
        <PendingTag change={change} onWithdraw={canEdit ? c => withdraw(row, c) : null} />
      </>
    );
  }

  function renderRow(r) {
    const jobs = r.Level === 'JOB' ? jobsOf(r) : [];
    const shown = jobs.slice(0, 3);
    const hidden = jobs.slice(3);
    const rowBusy = busy === r.uuid || !!r._pending?.optimistic;   // a save for this row is still on the wire
    return (
      <tr key={`${r.uuid}${r._inherited ? ':inh' : ''}`} className={`${r._pendingDelete ? 'nsx-row--deleting ' : ''}${r._inherited ? 'nsx-row--inherited' : ''}`}>
        <td className="nsx-col-narr">
          <NarrativeHtml html={r.Narrative} className={r._pendingDelete ? 'nsx-struck' : undefined} />
          {r._pending && <div className="br-pending-row">{renderPending(r)}</div>}
        </td>
        <td>
          {r.Level === 'SERV' ? (
            <span className="nsx-chip">{r.Serv} — service level</span>
          ) : (
            <div className="nsx-chips">
              {shown.map(l => <span key={l} className="nsx-chip">{l}</span>)}
              {hidden.length > 0 && <span className="nsx-chip nsx-chip--more" title={hidden.join('\n')}>+{hidden.length}</span>}
            </div>
          )}
        </td>
        <td><span className={`nsx-badge nsx-badge--${LEVEL_CLS[r.Level] || 'other'}`}>{r.Level}</span></td>
        <td><span className={`nsx-badge nsx-badge--${TYPE_CLS[r.Type] || 'other'}`}>{r.Type}</span></td>
        <td>
          <span className="nsx-badge" style={serviceStyle(r.Serv)} title={servTitles[r.Serv] || undefined}>{r.Serv}</span>
        </td>
        <td>
          {r._inherited
            ? <span className="nsx-badge nsx-badge--inherited" title="Set on the billing group parent; read-only here">Inherited from {r._inheritedFrom || 'parent'}</span>
            : (r.ApplyToGroup ? <span className="nsx-badge nsx-badge--group" title="Applies to every child in the billing group">All group</span> : null)}
        </td>
        {canEdit && (
          <td className="nsx-col-actions">
            {r._inherited ? (
              <span className="nsx-locked"><i className="fas fa-lock" /> Locked</span>
            ) : (
              <>
                <button type="button" className="cs-btn" disabled={rowBusy} onClick={() => setEditor({ row: r })}>Edit</button>
                {!r._pendingDelete && (
                  <button type="button" className="cs-btn cs-btn--danger" disabled={rowBusy} onClick={() => remove(r)}>Delete</button>
                )}
              </>
            )}
          </td>
        )}
      </tr>
    );
  }

  const title = `Narrative Standards — ${client?.clientCode || ''} ${client?.clientName || ''}`.trim();

  return (
    <>
      <div className="nsx-manager">
        <Modal
          open={!!open}
          onClose={onClose}
          title={title}
          wide
          footer={<button type="button" className="cs-btn" onClick={onClose}>Done</button>}
        >
          {initialLoading && !view && <div className="detail-empty">Loading narrative standards…</div>}

          {error && !view && (
            <div className="nsx-error">
              <i className="fas fa-exclamation-triangle" /> Couldn't load narrative standards: {error.message}{' '}
              <button type="button" className="cs-btn" onClick={() => refetch()}>Retry</button>
            </div>
          )}

          {view && (
            <div className="nsx-body">
              {error && <div className="nsx-warn"><i className="fas fa-exclamation-triangle" /> Couldn't refresh: {error.message}</div>}
              {locked && (
                <div className="nsx-warn">
                  <i className="fas fa-lock" /> You've signed off this client's review, so narrative standards are read-only.
                </div>
              )}
              {!locked && readOnly && (
                <div className="nsx-warn"><i className="fas fa-eye" /> View only — you can't change narrative standards for this client.</div>
              )}

              <div className="nsx-filters">
                <button type="button" className="nsx-pill" onClick={clearFilters} disabled={!filtersOn}>Clear</button>
                <select className="nsx-select nsx-select--sm" value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
                  <option value="">All Levels</option>
                  {uniqSorted(rows.map(r => r.Level)).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <select className="nsx-select nsx-select--sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                  <option value="">All Types</option>
                  {uniqSorted(rows.map(r => r.Type)).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <select className="nsx-select nsx-select--sm" value={servFilter} onChange={e => setServFilter(e.target.value)}>
                  <option value="">All Services</option>
                  {uniqSorted(rows.map(r => r.Serv)).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <input
                  type="text" className="nsx-search" placeholder="Search jobs or narratives…"
                  value={text} onChange={e => setText(e.target.value)}
                />
                <NarrativeStandardsHelp small />
                {canEdit && (
                  <button type="button" className="nsx-pill nsx-pill--primary" onClick={() => setEditor({ row: null })}>
                    <i className="fas fa-plus" /> New Narrative
                  </button>
                )}
              </div>

              {rows.length === 0 ? (
                <div className="detail-empty">No narrative standards for this client yet.</div>
              ) : filtered.length === 0 ? (
                <div className="detail-empty">No standards match the filters.</div>
              ) : (
                <table className="detail-table nsx-table">
                  <thead>
                    <tr>
                      <th>Narrative</th>
                      <th>Job / Service</th>
                      <th>Level</th>
                      <th>Type</th>
                      <th>Service</th>
                      <th>Group</th>
                      {canEdit && <th className="nsx-col-actions">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>{filtered.map(renderRow)}</tbody>
                </table>
              )}
            </div>
          )}
        </Modal>
      </div>

      {open && view && editor && (
        <NarrativeEditModal
          key={editor.row ? editor.row.uuid : 'new'}
          view={view}
          period={period}
          client={client}
          initial={editor.row}
          draft={editor.draft}
          onClose={() => setEditor(null)}
          onOptimistic={showSubmitted}
          onRefused={draft => setEditor({ row: editor.row, draft })}
          onSubmitted={refreshAll}
        />
      )}
    </>
  );
}
